import React from 'react';
import {
    Box,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import BootstrapDialog, {
    BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import * as invApi from '../../../lib/api/inventory';
import { listInventoryStocks } from '../../../modules/inventoryModule/invStocks';

const StockEditModal = ({ open, handleClose, data, whUuid }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const validationSchema = yup.object({
        productCnt: yup.number('수량은 숫자만 가능합니다')
            .min(0, '수량은 0 이상이어야 합니다')
            .required('수량을 입력하세요'),
        priceVal: yup.number('단가는 숫자만 가능합니다')
            .min(0, '단가는 0 이상이어야 합니다')
            .required('단가를 입력하세요'),
        trnDesc: yup.string().required('수정 사유를 입력하세요'),
    });

    const formik = useFormik({
        initialValues: {
            productCnt: data?.productCnt || 0,
            priceVal: data?.priceVal || 0,
            trnDesc: '',
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
                const res = await invApi.editStockInfo(whUuid, data.stockUuid, {
                    productCnt: String(values.productCnt),
                    priceVal: String(values.priceVal),
                    trnDesc: values.trnDesc,
                });
                if (res.data && res.data.result === 'success') {
                    enqueueSnackbar('재고가 수정되었습니다', { variant: 'success' });
                    dispatch(listInventoryStocks({ whUuid }));
                    handleClose();
                } else {
                    enqueueSnackbar(res.data?.message || '수정에 실패했습니다', { variant: 'error' });
                }
            } catch (err) {
                const message =
                    typeof err.response !== 'undefined'
                        ? err.response.data.message
                        : err.message;
                enqueueSnackbar(message, { variant: 'error' });
            }
            setSubmitting(false);
        },
    });

    if (!data) return null;

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="stock-edit-dialog"
            open={open}
            fullWidth
            maxWidth="sm"
        >
            <BootstrapDialogTitle id="stock-edit-dialog" onClose={handleClose}>
                재고 수정
            </BootstrapDialogTitle>
            <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">제품 정보</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="제품명"
                                value={data.productNm || ''}
                                InputProps={{ readOnly: true }}
                                variant="filled"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="모델명"
                                value={data.modelNm || ''}
                                InputProps={{ readOnly: true }}
                                variant="filled"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="제조사"
                                value={data.masPartnerNm || ''}
                                InputProps={{ readOnly: true }}
                                variant="filled"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="매입처"
                                value={data.pricePartnerNm || ''}
                                InputProps={{ readOnly: true }}
                                variant="filled"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="textSecondary">수정 항목</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                required
                                label="수량"
                                name="productCnt"
                                type="number"
                                value={formik.values.productCnt}
                                onChange={formik.handleChange}
                                error={formik.touched.productCnt && Boolean(formik.errors.productCnt)}
                                helperText={formik.touched.productCnt && formik.errors.productCnt}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                required
                                label="단가"
                                name="priceVal"
                                type="number"
                                value={formik.values.priceVal}
                                onChange={formik.handleChange}
                                error={formik.touched.priceVal && Boolean(formik.errors.priceVal)}
                                helperText={formik.touched.priceVal && formik.errors.priceVal}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="수정 사유"
                                name="trnDesc"
                                value={formik.values.trnDesc}
                                onChange={formik.handleChange}
                                error={formik.touched.trnDesc && Boolean(formik.errors.trnDesc)}
                                helperText={formik.touched.trnDesc && formik.errors.trnDesc}
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>취소</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting}
                    >
                        수정
                    </Button>
                </DialogActions>
            </Box>
        </BootstrapDialog>
    );
};

export default StockEditModal;
